import { indexedDBService } from "@/services/storage";
import { createZustandIndexedDBStorage } from "@/services/storage/zustandIndexedDBStorage";
import { useDataflowStore } from "@/stores/dataflow";
import type { DatabaseStore, Dataflow, Project } from "@/types/stores/database";
import { logger } from "@/utils/debug";
import { create } from "zustand";
import { createJSONStorage, devtools, persist } from "zustand/middleware";

const initialState = {
  projects: [],
  loading: true,
  error: null,
};

export const useDatabaseStore = create<DatabaseStore>()(
  devtools(
    persist(
      (set, get) => ({
        ...initialState,

        setProjects: (projects) =>
          set({ projects }, false, "database/setProjects"),

        setLoading: (loading) => set({ loading }, false, "database/setLoading"),

        setError: (error) => set({ error }, false, "database/setError"),

        loadProjects: async () => {
          try {
            set({ loading: true, error: null }, false, "database/loadProjects");

            const currentState = get();
            const existingProjectsMap = new Map(
              currentState.projects.map((p) => [p.id, p])
            );

            const projectsData = await indexedDBService.getAllProjects();

            const transformedProjects: Project[] = [];
            for (let i = 0; i < projectsData.length; i++) {
              const project = projectsData[i];
              const dataflows = await indexedDBService.getDataflowsByProject(
                project.id
              );
              transformedProjects.push({
                ...project,
                dataflows: dataflows || [],
              });

              if (i % 10 === 0 && i > 0) {
                await new Promise((resolve) => setTimeout(resolve, 0));
              }
            }

            const projectsFromIndexedDB = new Map(
              transformedProjects.map((p) => [p.id, p])
            );

            for (const [id, project] of existingProjectsMap) {
              if (!projectsFromIndexedDB.has(id)) {
                logger.log(
                  `[loadProjects] Merging project from Zustand persist: ${project.title}`
                );
                transformedProjects.push(project);
              }
            }

            const dataflowStore = useDataflowStore.getState();
            let processedCount = 0;

            for (const project of transformedProjects) {
              for (const dataflow of project.dataflows) {
                if (
                  dataflow.dataflow_id &&
                  !dataflowStore.dataflows[dataflow.dataflow_id]
                ) {
                  dataflowStore.addDataflow(dataflow.dataflow_id, {
                    ...dataflow,
                    dataflow_name: dataflow.dataflow_name || undefined,
                    dataflow_description:
                      dataflow.dataflow_description || undefined,
                    flow_definition_json:
                      (dataflow.flow_definition_json as any) || [],
                  });
                  processedCount++;

                  if (processedCount % 50 === 0) {
                    await new Promise((resolve) => setTimeout(resolve, 0));
                  }
                }
              }
            }

            set(
              { projects: transformedProjects, loading: false },
              false,
              "database/loadProjects"
            );
          } catch (err) {
            logger.error("Error loading projects:", err);
            set(
              {
                error:
                  err instanceof Error
                    ? err.message
                    : "Failed to load projects",
                loading: false,
              },
              false,
              "database/loadProjects"
            );
          }
        },

        createProject: async (projectData) => {
          try {
            set({ error: null }, false, "database/createProject");

            if (!projectData.title || projectData.title.trim().length === 0) {
              throw new Error("Project title is required");
            }

            await new Promise((resolve) => setTimeout(resolve, 1000));

            const id = crypto.randomUUID();
            const now = new Date().toISOString();

            const newProject: Project = {
              id,
              title: projectData.title.trim(),
              description: projectData.description || null,
              created_at: projectData.created_at || now,
              updated_at: projectData.updated_at || now,
              dataflows: [],
            };

            set(
              (state) => ({
                projects: [newProject, ...state.projects],
              }),
              false,
              "database/createProject"
            );

            try {
              const projectData = {
                id: newProject.id,
                title: newProject.title,
                description: newProject.description,
                created_at: newProject.created_at,
                updated_at: newProject.updated_at,
              };
              await indexedDBService.storeProject(projectData);
            } catch (dbError) {
              logger.error("[createProject] IndexedDB error:", dbError);
              logger.error("[createProject] IndexedDB error details:", {
                message:
                  dbError instanceof Error ? dbError.message : String(dbError),
                stack: dbError instanceof Error ? dbError.stack : undefined,
              });
              throw dbError;
            }
            return { data: newProject, error: null };
          } catch (err) {
            logger.error("[createProject] Error creating project:", err);
            const errorMessage =
              err instanceof Error ? err.message : "Failed to create project";
            set({ error: errorMessage }, false, "database/createProject");
            return { data: null, error: new Error(errorMessage) };
          }
        },

        updateProject: async (id, updates) => {
          try {
            set({ error: null }, false, "database/updateProject");

            const existingProject = await indexedDBService.getProject(id);
            if (!existingProject) {
              throw new Error("Project not found");
            }

            const updatedProject = {
              ...existingProject,
              ...updates,
              updated_at: new Date().toISOString(),
            };

            await indexedDBService.storeProject(updatedProject);

            const state = get();
            const currentProject = state.projects.find((p) => p.id === id);
            const projectWithDataflows: Project = {
              ...updatedProject,
              dataflows: currentProject?.dataflows || [],
            };

            set(
              (state) => ({
                projects: state.projects.map((project) =>
                  project.id === id ? projectWithDataflows : project
                ),
              }),
              false,
              "database/updateProject"
            );

            return { data: projectWithDataflows, error: null };
          } catch (err) {
            logger.error("Error updating project:", err);
            const errorMessage =
              err instanceof Error ? err.message : "Failed to update project";
            set({ error: errorMessage }, false, "database/updateProject");
            return { data: null, error: err };
          }
        },

        deleteProject: async (id) => {
          try {
            set({ error: null }, false, "database/deleteProject");

            await indexedDBService.deleteDataflowsByProject(id);

            await indexedDBService.deleteProject(id);

            set(
              (state) => ({
                projects: state.projects.filter((project) => project.id !== id),
              }),
              false,
              "database/deleteProject"
            );

            return { error: null };
          } catch (err) {
            logger.error("Error deleting project:", err);
            const errorMessage =
              err instanceof Error ? err.message : "Failed to delete project";
            set({ error: errorMessage }, false, "database/deleteProject");
            return { error: err };
          }
        },

        createDataflow: async (dataflowData) => {
          try {
            set({ error: null }, false, "database/createDataflow");

            await new Promise((resolve) => setTimeout(resolve, 1000));

            const dataflowId = dataflowData.dataflow_id || crypto.randomUUID();
            const now = new Date().toISOString();

            const newDataflow: Dataflow = {
              dataflow_id: dataflowId,
              dataflow_name: dataflowData.dataflow_name || null,
              dataflow_description: dataflowData.dataflow_description || null,
              project_id: dataflowData.project_id,
              flow_definition_json: dataflowData.flow_definition_json || null,
              status: dataflowData.status || "draft",
              datetime_created: dataflowData.datetime_created || now,
              datetime_updated: dataflowData.datetime_updated || now,
            };

            await indexedDBService.storeDataflow(newDataflow);

            set(
              (state) => ({
                projects: state.projects.map((project) =>
                  project.id === dataflowData.project_id
                    ? {
                        ...project,
                        dataflows: [...project.dataflows, newDataflow],
                      }
                    : project
                ),
              }),
              false,
              "database/createDataflow"
            );

            return { data: newDataflow, error: null };
          } catch (err) {
            logger.error("Error creating dataflow:", err);
            const errorMessage =
              err instanceof Error ? err.message : "Failed to create workspace";
            set({ error: errorMessage }, false, "database/createDataflow");
            return { data: null, error: err };
          }
        },

        updateDataflow: async (id, updates) => {
          try {
            set({ error: null }, false, "database/updateDataflow");

            const existingDataflow = await indexedDBService.getDataflow(id);
            if (!existingDataflow) {
              throw new Error("Dataflow not found");
            }

            const updatedDataflow: Dataflow = {
              ...existingDataflow,
              ...updates,
              datetime_updated: new Date().toISOString(),
            };

            await indexedDBService.storeDataflow(updatedDataflow);

            set(
              (state) => ({
                projects: state.projects.map((project) => ({
                  ...project,
                  dataflows: project.dataflows.map((dataflow) =>
                    dataflow.dataflow_id === id ? updatedDataflow : dataflow
                  ),
                })),
              }),
              false,
              "database/updateDataflow"
            );

            return { data: updatedDataflow, error: null };
          } catch (err) {
            logger.error("Error updating dataflow:", err);
            const errorMessage =
              err instanceof Error ? err.message : "Failed to update dataflow";
            set({ error: errorMessage }, false, "database/updateDataflow");
            return { data: null, error: err };
          }
        },

        deleteDataflow: async (id) => {
          try {
            set({ error: null }, false, "database/deleteDataflow");

            await indexedDBService.deleteDataflow(id);

            set(
              (state) => ({
                projects: state.projects.map((project) => ({
                  ...project,
                  dataflows: project.dataflows.filter(
                    (dataflow) => dataflow.dataflow_id !== id
                  ),
                })),
              }),
              false,
              "database/deleteDataflow"
            );

            return { error: null };
          } catch (err) {
            logger.error("Error deleting dataflow:", err);
            const errorMessage =
              err instanceof Error ? err.message : "Failed to delete dataflow";
            set({ error: errorMessage }, false, "database/deleteDataflow");
            return { error: err };
          }
        },

        getProjectById: (id) => {
          return get().projects.find((project) => project.id === id);
        },

        getDataflowById: (id) => {
          for (const project of get().projects) {
            const dataflow = project.dataflows.find(
              (dataflow) => dataflow.dataflow_id === id
            );
            if (dataflow) return dataflow;
          }
          return undefined;
        },

        refreshProjects: async () => {
          await get().loadProjects();
        },
      }),
      {
        name: "database-store",
        storage: createJSONStorage(createZustandIndexedDBStorage("projects")),
        partialize: (state) => ({
          projects: state.projects,
          loading: state.loading,
          error: state.error,
        }),
      }
    ),
    {
      name: "database-store",
    }
  )
);

export const useProjects = () => useDatabaseStore((state) => state.projects);
export const useDatabaseLoading = () =>
  useDatabaseStore((state) => state.loading);
export const useDatabaseError = () => useDatabaseStore((state) => state.error);
export const useLoadProjects = () =>
  useDatabaseStore((state) => state.loadProjects);
export const useCreateProject = () =>
  useDatabaseStore((state) => state.createProject);
export const useUpdateProject = () =>
  useDatabaseStore((state) => state.updateProject);
export const useDeleteProject = () =>
  useDatabaseStore((state) => state.deleteProject);
export const useCreateDataflow = () =>
  useDatabaseStore((state) => state.createDataflow);
export const useUpdateDataflow = () =>
  useDatabaseStore((state) => state.updateDataflow);
export const useDeleteDataflow = () =>
  useDatabaseStore((state) => state.deleteDataflow);
export const useGetProjectById = () =>
  useDatabaseStore((state) => state.getProjectById);
export const useGetDataflowById = () =>
  useDatabaseStore((state) => state.getDataflowById);
export const useDatabase = () => useDatabaseStore();
